import { firebaseAuth } from './firebase'

export const propertyApi = (import.meta.env.VITE_PROPERTY_API_URL || 'https://api.dihaw.ai').replace(/\/$/, '')
const uploadsApi = (import.meta.env.VITE_UPLOAD_API_URL || 'https://api.dihaw.ai').replace(/\/$/, '')

export async function propertyFetch<T>(path: string, options: { method?: string; body?: unknown; public?: boolean; origin?: string; signal?: AbortSignal } = {}): Promise<T> {
  const headers = new Headers({ Accept: 'application/json' })
  if (!options.public) {
    await firebaseAuth.authStateReady()
    const user = firebaseAuth.currentUser
    if (!user) throw Object.assign(new Error('Silakan masuk terlebih dahulu.'), { code: 'unauthenticated' })
    headers.set('Authorization', `Bearer ${await user.getIdToken()}`)
  }
  if (options.body !== undefined) headers.set('Content-Type', 'application/json')
  if (options.method && options.method !== 'GET') headers.set('Idempotency-Key', crypto.randomUUID())
  const response = await fetch(`${options.origin || propertyApi}${path}`, {
    method: options.method || 'GET', headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    signal: options.signal || AbortSignal.timeout(90000),
  })
  const result = await response.json()
  if (!response.ok) throw Object.assign(new Error(result.error?.message || 'Permintaan belum berhasil. Coba lagi nanti.'), { code: result.error?.code || 'unavailable', status: response.status })
  return result as T
}

type Endpoint = { method: string; path: string; public?: boolean; origin?: string }
const operations: Record<string, Endpoint> = {
  propertyAnalyticsDashboard: { method: 'GET', path: '/v1/property/me/analytics' },
  propertyAnalyticsTrack: { method: 'POST', path: '/v1/property/listings/:listingId/events', public: true },
  propertyLeadSubmit: { method: 'POST', path: '/v1/property/listings/:listingId/leads', public: true },
  propertyAgentCheckout: { method: 'POST', path: '/v1/property/me/agent-checkouts' },
  propertyFeaturedCheckout: { method: 'POST', path: '/v1/property/me/listings/:listingId/featured-checkouts' },
  propertyAiCreditTopupCheckout: { method: 'POST', path: '/v1/property/me/credit-checkouts' },
  propertyReceiptDownload: { method: 'GET', path: '/v1/property/me/payments/:referenceId/receipt' },
  propertyAiImageEnhance: { method: 'POST', path: '/v1/property/me/image-jobs' },
  propertyListingDraftGenerate: { method: 'POST', path: '/v1/property/me/listing-drafts' },
  propertyListingCaptionGenerate: { method: 'POST', path: '/v1/property/me/listing-captions' },
  propertyListingInsightGenerate: { method: 'POST', path: '/v1/property/listings/:listingId/insights', public: true },
  cloudflareR2Upload: { method: 'POST', path: '/v1/uploads', origin: uploadsApi },
  socialPublicBlogsList: { method: 'GET', path: '/v1/property/articles', public: true },
  socialPublicBlogReadTrack: { method: 'POST', path: '/v1/property/articles/:blogId/reads', public: true },
}

// Keeps component response contracts stable while every operation uses a native REST resource.
export function propertyOperation<Request = unknown, Result = unknown>(_api: string, name: string) {
  const endpoint = operations[name]
  if (!endpoint) throw new Error(`Unknown property operation: ${name}`)
  return async (payload: Request): Promise<{ data: Result }> => {
    const body = { ...(payload as Record<string, unknown>) }
    if (name === 'propertyListingInsightGenerate') body.listingId = (body.listing as Record<string, unknown> | undefined)?.id
    const path = endpoint.path.replace(/:([A-Za-z]+)/g, (_, field: string) => {
      const value = body[field]
      if (typeof value !== 'string' || !value.trim()) throw new Error('Identitas resource belum lengkap.')
      delete body[field]
      return encodeURIComponent(value)
    })
    const query = new URLSearchParams()
    if (endpoint.method === 'GET') Object.entries(body).forEach(([key, value]) => { if (value !== undefined && value !== null) query.set(key, String(value)) })
    const suffix = query.size ? `?${query}` : ''
    const data = await propertyFetch<Result>(`${path}${suffix}`, { ...endpoint, ...(endpoint.method === 'GET' ? {} : { body }) })
    return { data }
  }
}
